import type { Point, Wall, Room } from '../types';
import { pointsAreEqual, isPointInsideRoom, getRoomBounds } from './floorPlanUtils';

/**
 * Professional collision detection system for room resizing
 * Prevents room overlaps by detecting collisions and constraining resize operations
 */

// Connection lock state - persists between drag operations
let connectionLockState: {
  isLocked: boolean;
  connectionPoint: Point | null;
  lockedRoomId: string | null;
  lockedWallId: string | null;
  lockStartTime: number;
} = {
  isLocked: false,
  connectionPoint: null,
  lockedRoomId: null,
  lockedWallId: null,
  lockStartTime: 0
};

/**
 * Reset connection lock state (call when starting a new drag operation)
 */
export const resetConnectionLock = () => {
  connectionLockState = {
    isLocked: false,
    connectionPoint: null,
    lockedRoomId: null,
    lockedWallId: null,
    lockStartTime: 0
  };
  console.log('🔒 CONNECTION LOCK RESET');
};

/**
 * Check if a connection should be locked based on hysteresis
 */
const shouldLockConnection = (
  targetPoint: Point,
  proposedConnectionPoint: Point,
  currentlyLocked: boolean
): boolean => {
  const distanceToConnection = Math.hypot(
    targetPoint.x - proposedConnectionPoint.x,
    targetPoint.y - proposedConnectionPoint.y
  );
  
  if (currentlyLocked) {
    // Hysteresis: require larger distance to break connection
    const breakDistance = 60; // Must drag 60px away to break connection
    return distanceToConnection < breakDistance;
  } else {
    // Standard snap distance to create connection
    const snapDistance = 25;
    return distanceToConnection < snapDistance;
  }
};

export interface CollisionResult {
  hasCollision: boolean;
  constrainedPoint: Point;
  collisionRooms: Room[];
  maxAllowedResize: number;
  collisionWalls: Wall[];
  isConnected?: boolean;
  connectionLocked?: boolean;
  connectionPoint?: Point;
}

/**
 * Check if a point is inside any room
 * Enhanced to handle complex room shapes with extrusions
 */
const isPointInsideAnyRoom = (point: Point, rooms: Room[], excludeRoom: Room): boolean => {
  return rooms.some(room => {
    if (room.stableId === excludeRoom.stableId) return false;
    
    // For complex rooms with extrusions, check if point is inside the room boundary
    // This handles rooms that aren't simple rectangular shapes
    try {
      return isPointInsideRoom(point, room.walls);
    } catch (error) {
      // If room shape is too complex for point-in-polygon, use bounding box as fallback
      const bounds = getRoomBounds(room.walls);
      return point.x >= bounds.minX && point.x <= bounds.maxX && 
             point.y >= bounds.minY && point.y <= bounds.maxY;
    }
  });
};

/**
 * Check if a wall segment intersects with any room boundaries
 * Enhanced to handle complex room shapes and provide snap zones
 */
const doesWallIntersectRooms = (wall: Wall, rooms: Room[], excludeRoom: Room, snapZone: number = 2): { intersects: boolean; intersectingRooms: Room[]; isInSnapZone: boolean } => {
  const intersectingRooms: Room[] = [];
  let isInSnapZone = false;
  
  for (const room of rooms) {
    if (room.stableId === excludeRoom.stableId) continue;
    
    // Check for actual intersections first
    let hasIntersection = false;
    let minDistance = Infinity;
    
    for (const roomWall of room.walls) {
      // Check for line intersection
      if (doLinesIntersect(wall.start, wall.end, roomWall.start, roomWall.end)) {
        intersectingRooms.push(room);
        hasIntersection = true;
        break;
      }
      
      // Calculate minimum distance for snap zone detection
      const distStart = pointToLineDistance(wall.start, roomWall.start, roomWall.end);
      const distEnd = pointToLineDistance(wall.end, roomWall.start, roomWall.end);
      minDistance = Math.min(minDistance, distStart, distEnd);
    }
    
    if (hasIntersection) continue;
    
    // Check if wall endpoints are inside the room (actual collision)
    // Enhanced to handle complex room shapes
    try {
      const startInside = isPointInsideRoom(wall.start, room.walls);
      const endInside = isPointInsideRoom(wall.end, room.walls);
      
      if (startInside || endInside) {
        if (!intersectingRooms.includes(room)) {
          intersectingRooms.push(room);
        }
      } else if (minDistance <= snapZone) {
        // Wall is in snap zone but not intersecting
        isInSnapZone = true;
      }
    } catch (error) {
      // Fallback for complex room shapes - use bounding box check
      const bounds = getRoomBounds(room.walls);
      const startInBounds = wall.start.x >= bounds.minX && wall.start.x <= bounds.maxX && 
                           wall.start.y >= bounds.minY && wall.start.y <= bounds.maxY;
      const endInBounds = wall.end.x >= bounds.minX && wall.end.x <= bounds.maxX && 
                         wall.end.y >= bounds.minY && wall.end.y <= bounds.maxY;
      
      if (startInBounds || endInBounds) {
        if (!intersectingRooms.includes(room)) {
          intersectingRooms.push(room);
        }
      } else if (minDistance <= snapZone) {
        isInSnapZone = true;
      }
    }
  }
  
  return {
    intersects: intersectingRooms.length > 0,
    intersectingRooms,
    isInSnapZone
  };
};

/**
 * Simple line intersection test
 */
const doLinesIntersect = (p1: Point, p2: Point, p3: Point, p4: Point): boolean => {
  const denom = (p4.y - p3.y) * (p2.x - p1.x) - (p4.x - p3.x) * (p2.y - p1.y);
  if (Math.abs(denom) < 0.0001) return false; // Lines are parallel
  
  const ua = ((p4.x - p3.x) * (p1.y - p3.y) - (p4.y - p3.y) * (p1.x - p3.x)) / denom;
  const ub = ((p2.x - p1.x) * (p1.y - p3.y) - (p2.y - p1.y) * (p1.x - p3.x)) / denom;
  
  return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
};

/**
 * Calculate the minimum distance from a point to a line segment
 */
const pointToLineDistance = (point: Point, lineStart: Point, lineEnd: Point): number => {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  
  if (length === 0) {
    // Line is actually a point
    return Math.sqrt((point.x - lineStart.x) ** 2 + (point.y - lineStart.y) ** 2);
  }
  
  // Calculate the parameter t for the closest point on the line segment
  const t = Math.max(0, Math.min(1, ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / (length * length)));
  
  // Calculate the closest point on the line segment
  const closestX = lineStart.x + t * dx;
  const closestY = lineStart.y + t * dy;
  
  // Return the distance from the point to the closest point on the line segment
  return Math.sqrt((point.x - closestX) ** 2 + (point.y - closestY) ** 2);
};

/**
 * Find the closest point on a line segment to a given point
 */
const getClosestPointOnLine = (point: Point, lineStart: Point, lineEnd: Point): Point => {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  
  if (length === 0) {
    // Line is actually a point
    return { x: lineStart.x, y: lineStart.y };
  }
  
  // Calculate the parameter t for the closest point on the line segment
  const t = Math.max(0, Math.min(1, ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / (length * length)));
  
  // Calculate the closest point on the line segment
  return {
    x: lineStart.x + t * dx,
    y: lineStart.y + t * dy
  };
};

/**
 * Find the best connection point for wall snapping during resize operations
 */
const findBestConnectionPoint = (
  proposedWall: Wall, 
  otherRooms: Room[], 
  snapZoneDistance: number,
  resizeDirection: string,
  originalTargetPoint: Point
): Point | null => {
  let bestConnectionPoint: Point | null = null;
  let minDistance = Infinity;
  
  console.log('🔍 FINDING CONNECTION POINT', {
    proposedWallCoords: `(${proposedWall.start.x.toFixed(1)},${proposedWall.start.y.toFixed(1)})->(${proposedWall.end.x.toFixed(1)},${proposedWall.end.y.toFixed(1)})`,
    resizeDirection,
    originalTarget: `(${originalTargetPoint.x.toFixed(1)},${originalTargetPoint.y.toFixed(1)})`,
    snapZone: snapZoneDistance
  });
  
  for (const room of otherRooms) {
    for (const roomWall of room.walls) {
      // Check for wall-to-wall parallel alignment (walls running in same direction)
      const isParallelAlignment = checkParallelWallAlignment(proposedWall, roomWall, resizeDirection);
      
      if (isParallelAlignment) {
        const connectionPoint = findParallelWallConnectionPoint(
          proposedWall, 
          roomWall, 
          resizeDirection, 
          originalTargetPoint, 
          snapZoneDistance
        );
        
        if (connectionPoint) {
          const distance = Math.hypot(
            originalTargetPoint.x - connectionPoint.x, 
            originalTargetPoint.y - connectionPoint.y
          );
          
          if (distance <= snapZoneDistance && distance < minDistance) {
            minDistance = distance;
            bestConnectionPoint = connectionPoint;
            
            console.log('🎯 PARALLEL CONNECTION FOUND', {
              connectionPoint: `(${connectionPoint.x.toFixed(1)},${connectionPoint.y.toFixed(1)})`,
              distance: distance.toFixed(1),
              roomWallCoords: `(${roomWall.start.x.toFixed(1)},${roomWall.start.y.toFixed(1)})->(${roomWall.end.x.toFixed(1)},${roomWall.end.y.toFixed(1)})`
            });
          }
        }
      }
      
      // Check for perpendicular wall connections (end-to-wall connections)
      const perpendicularConnection = findPerpendicularWallConnectionPoint(
        proposedWall,
        roomWall,
        resizeDirection,
        originalTargetPoint,
        snapZoneDistance
      );
      
      if (perpendicularConnection) {
        const distance = Math.hypot(
          originalTargetPoint.x - perpendicularConnection.x,
          originalTargetPoint.y - perpendicularConnection.y
        );
        
        if (distance <= snapZoneDistance && distance < minDistance) {
          minDistance = distance;
          bestConnectionPoint = perpendicularConnection;
          
          console.log('🔄 PERPENDICULAR CONNECTION FOUND', {
            connectionPoint: `(${perpendicularConnection.x.toFixed(1)},${perpendicularConnection.y.toFixed(1)})`,
            distance: distance.toFixed(1),
            roomWallCoords: `(${roomWall.start.x.toFixed(1)},${roomWall.start.y.toFixed(1)})->(${roomWall.end.x.toFixed(1)},${roomWall.end.y.toFixed(1)})`
          });
        }
      }
      
      // AGGRESSIVE FALLBACK: Simple distance-based snapping to nearest wall point
      const roomWallPoints = [roomWall.start, roomWall.end];
      for (const roomPoint of roomWallPoints) {
        const distance = Math.hypot(
          originalTargetPoint.x - roomPoint.x,
          originalTargetPoint.y - roomPoint.y
        );
        
        if (distance <= snapZoneDistance && distance < minDistance) {
          // Calculate the connection point based on resize direction
          let snapConnection: Point | null = null;
          
          if (resizeDirection === 'e' || resizeDirection === 'w') {
            // Horizontal resize - snap X coordinate to room point
            snapConnection = { x: roomPoint.x, y: originalTargetPoint.y };
          } else if (resizeDirection === 'n' || resizeDirection === 's') {
            // Vertical resize - snap Y coordinate to room point
            snapConnection = { x: originalTargetPoint.x, y: roomPoint.y };
          } else {
            // For other directions, snap directly to the room point
            snapConnection = roomPoint;
          }
          
          if (snapConnection) {
            minDistance = distance;
            bestConnectionPoint = snapConnection;
            
            console.log('🔥 AGGRESSIVE SNAP CONNECTION', {
              originalTarget: `(${originalTargetPoint.x.toFixed(1)},${originalTargetPoint.y.toFixed(1)})`,
              roomPoint: `(${roomPoint.x.toFixed(1)},${roomPoint.y.toFixed(1)})`,
              snapConnection: `(${snapConnection.x.toFixed(1)},${snapConnection.y.toFixed(1)})`,
              distance: distance.toFixed(1),
              resizeDirection
            });
          }
        }
      }
    }
  }
  
  return bestConnectionPoint;
};

/**
 * Check if walls are parallel and could be aligned
 */
const checkParallelWallAlignment = (wall1: Wall, wall2: Wall, resizeDirection: string): boolean => {
  const wall1Dx = wall1.end.x - wall1.start.x;
  const wall1Dy = wall1.end.y - wall1.start.y;
  const wall2Dx = wall2.end.x - wall2.start.x;
  const wall2Dy = wall2.end.y - wall2.start.y;
  
  // Check if walls are roughly parallel (within 45 degrees - much more lenient)
  const wall1Angle = Math.atan2(wall1Dy, wall1Dx);
  const wall2Angle = Math.atan2(wall2Dy, wall2Dx);
  const angleDiff = Math.abs(wall1Angle - wall2Angle);
  const parallelThreshold = Math.PI / 4; // 45 degrees - much more permissive
  
  return angleDiff < parallelThreshold || Math.abs(angleDiff - Math.PI) < parallelThreshold;
};

/**
 * Find connection point for parallel walls
 */
const findParallelWallConnectionPoint = (
  proposedWall: Wall,
  roomWall: Wall,
  resizeDirection: string,
  originalTarget: Point,
  snapZone: number
): Point | null => {
  // For parallel walls, we want to align them so they connect end-to-end or overlap
  const tolerance = snapZone;
  
  // Check if walls can be aligned along their parallel direction
  if (resizeDirection === 'e' || resizeDirection === 'w') {
    // Horizontal resize - check for vertical alignment and snap to closest room wall X position
    const yDiff1 = Math.abs(proposedWall.start.y - roomWall.start.y);
    const yDiff2 = Math.abs(proposedWall.end.y - roomWall.start.y);
    const yDiff3 = Math.abs(proposedWall.start.y - roomWall.end.y);
    const yDiff4 = Math.abs(proposedWall.end.y - roomWall.end.y);
    
    const minYDiff = Math.min(yDiff1, yDiff2, yDiff3, yDiff4);
    
    if (minYDiff <= tolerance) {
      // Walls are close enough vertically, snap to connect them horizontally
      if (resizeDirection === 'e') {
        return { x: Math.max(roomWall.start.x, roomWall.end.x), y: originalTarget.y };
      } else {
        return { x: Math.min(roomWall.start.x, roomWall.end.x), y: originalTarget.y };
      }
    }
  } else if (resizeDirection === 'n' || resizeDirection === 's') {
    // Vertical resize - check for horizontal alignment and snap to closest room wall Y position
    const xDiff1 = Math.abs(proposedWall.start.x - roomWall.start.x);
    const xDiff2 = Math.abs(proposedWall.end.x - roomWall.start.x);
    const xDiff3 = Math.abs(proposedWall.start.x - roomWall.end.x);
    const xDiff4 = Math.abs(proposedWall.end.x - roomWall.end.x);
    
    const minXDiff = Math.min(xDiff1, xDiff2, xDiff3, xDiff4);
    
    if (minXDiff <= tolerance) {
      // Walls are close enough horizontally, snap to connect them vertically
      if (resizeDirection === 's') {
        return { x: originalTarget.x, y: Math.max(roomWall.start.y, roomWall.end.y) };
      } else {
        return { x: originalTarget.x, y: Math.min(roomWall.start.y, roomWall.end.y) };
      }
    }
  }
  
  return null;
};

/**
 * Find connection point for perpendicular walls
 */
const findPerpendicularWallConnectionPoint = (
  proposedWall: Wall,
  roomWall: Wall,
  resizeDirection: string,
  originalTarget: Point,
  snapZone: number
): Point | null => {
  // Check if proposed wall endpoint is close to room wall
  const proposedStart = proposedWall.start;
  const proposedEnd = proposedWall.end;
  
  // Find closest point on room wall to proposed wall endpoints
  const closestToStart = getClosestPointOnLine(proposedStart, roomWall.start, roomWall.end);
  const closestToEnd = getClosestPointOnLine(proposedEnd, roomWall.start, roomWall.end);
  
  const distToStart = Math.hypot(proposedStart.x - closestToStart.x, proposedStart.y - closestToStart.y);
  const distToEnd = Math.hypot(proposedEnd.x - closestToEnd.x, proposedEnd.y - closestToEnd.y);
  
  if (distToStart <= snapZone) {
    // Connect via start point
    const deltaX = closestToStart.x - proposedStart.x;
    const deltaY = closestToStart.y - proposedStart.y;
    return {
      x: originalTarget.x + deltaX,
      y: originalTarget.y + deltaY
    };
  }
  
  if (distToEnd <= snapZone) {
    // Connect via end point  
    const deltaX = closestToEnd.x - proposedEnd.x;
    const deltaY = closestToEnd.y - proposedEnd.y;
    return {
      x: originalTarget.x + deltaX,
      y: originalTarget.y + deltaY
    };
  }
  
  return null;
};

/**
 * Find immediate snap point - simple, aggressive snapping to nearest wall point
 */
const findImmediateSnapPoint = (
  targetPoint: Point,
  otherRooms: Room[],
  resizeDirection: string,
  snapDistance: number
): Point | null => {
  let bestSnapPoint: Point | null = null;
  let minDistance = Infinity;
  
  // PRIORITY 1: Check for very close wall endpoints first (within 15px)
  const priorityDistance = Math.min(15, snapDistance * 0.6);
  
  for (const room of otherRooms) {
    for (const wall of room.walls) {
      const wallPoints = [wall.start, wall.end];
      
      for (const wallPoint of wallPoints) {
        const distance = Math.hypot(targetPoint.x - wallPoint.x, targetPoint.y - wallPoint.y);
        
        // Priority snap for very close points
        if (distance <= priorityDistance) {
          let snapPoint: Point;
          
          switch (resizeDirection) {
            case 'e': // East - moving right
            case 'w': // West - moving left
              snapPoint = { x: wallPoint.x, y: targetPoint.y };
              break;
            case 'n': // North - moving up
            case 's': // South - moving down
              snapPoint = { x: targetPoint.x, y: wallPoint.y };
              break;
            default:
              snapPoint = wallPoint;
              break;
          }
          
          console.log('🎯 PRIORITY SNAP TRIGGERED', {
            distance: distance.toFixed(1),
            wallPoint: `(${wallPoint.x.toFixed(1)},${wallPoint.y.toFixed(1)})`,
            snapPoint: `(${snapPoint.x.toFixed(1)},${snapPoint.y.toFixed(1)})`,
            resizeDirection
          });
          
          return snapPoint; // Return immediately for priority snaps
        }
        
        // Regular snap detection for larger distances
        if (distance <= snapDistance && distance < minDistance) {
          let snapPoint: Point;
          
          switch (resizeDirection) {
            case 'e': // East - moving right
            case 'w': // West - moving left
              snapPoint = { x: wallPoint.x, y: targetPoint.y };
              break;
            case 'n': // North - moving up
            case 's': // South - moving down
              snapPoint = { x: targetPoint.x, y: wallPoint.y };
              break;
            default:
              snapPoint = wallPoint;
              break;
          }
          
          minDistance = distance;
          bestSnapPoint = snapPoint;
        }
      }
      
      // PRIORITY 2: Also check for alignment with wall segments (closer than endpoints)
      const closestPointOnWall = getClosestPointOnLine(targetPoint, wall.start, wall.end);
      const wallSegmentDistance = Math.hypot(
        targetPoint.x - closestPointOnWall.x, 
        targetPoint.y - closestPointOnWall.y
      );
      
      if (wallSegmentDistance <= priorityDistance) {
        let snapPoint: Point;
        
        switch (resizeDirection) {
          case 'e': // East - moving right
          case 'w': // West - moving left
            snapPoint = { x: closestPointOnWall.x, y: targetPoint.y };
            break;
          case 'n': // North - moving up
          case 's': // South - moving down
            snapPoint = { x: targetPoint.x, y: closestPointOnWall.y };
            break;
          default:
            snapPoint = closestPointOnWall;
            break;
        }
        
        console.log('🎯 WALL SEGMENT SNAP TRIGGERED', {
          distance: wallSegmentDistance.toFixed(1),
          closestPoint: `(${closestPointOnWall.x.toFixed(1)},${closestPointOnWall.y.toFixed(1)})`,
          snapPoint: `(${snapPoint.x.toFixed(1)},${snapPoint.y.toFixed(1)})`,
          resizeDirection
        });
        
        return snapPoint; // Return immediately for wall segment snaps
      }
    }
  }
  
  return bestSnapPoint;
};

/**
 * Find the maximum allowed resize distance before collision
 */
const findMaxAllowedResize = (
  resizingRoom: Room,
  resizeDirection: string,
  draggedWall: Wall,
  targetPoint: Point,
  otherRooms: Room[],
  minDistance: number = 5 // Minimum distance to maintain between rooms
): number => {
  const originalPoint = (() => {
    switch (resizeDirection) {
      case 'w': // West - moving left edge
      case 'e': // East - moving right edge
        return { x: draggedWall.start.x, y: draggedWall.start.y };
      case 'n': // North - moving top edge
      case 's': // South - moving bottom edge
        return { x: draggedWall.start.x, y: draggedWall.start.y };
      default:
        // For complex resize directions, use the start point
        return { x: draggedWall.start.x, y: draggedWall.start.y };
    }
  })();
    
  const deltaX = targetPoint.x - originalPoint.x;
  const deltaY = targetPoint.y - originalPoint.y;
  const totalDistance = Math.hypot(deltaX, deltaY);
  
  if (totalDistance === 0) return 0;
  
  let maxDistance = totalDistance;
  
  // 🚨 BUG FIX: Detect shared walls early to apply relaxed collision rules
  // Detect if this is a shared wall resize by checking if dragged wall is on the boundary of other rooms
  const isSharedWallResize = otherRooms.some(room => {
    return room.walls.some(wall => {
      // Check if walls are collinear using cross product
      const draggedVector = {
        x: draggedWall.end.x - draggedWall.start.x,
        y: draggedWall.end.y - draggedWall.start.y
      };
      const roomWallVector = {
        x: wall.end.x - wall.start.x,
        y: wall.end.y - wall.start.y
      };
      
      // Cross product to check if vectors are parallel (collinear)
      const crossProduct = Math.abs(draggedVector.x * roomWallVector.y - draggedVector.y * roomWallVector.x);
      const isCollinear = crossProduct < 1; // Small tolerance for floating point
      
      if (!isCollinear) return false;
      
      // Determine if walls are horizontal or vertical for proper overlap check
      const isHorizontal = Math.abs(draggedVector.y) < Math.abs(draggedVector.x);
      
      let draggedMin, draggedMax, roomWallMin, roomWallMax;
      
      if (isHorizontal) {
        // For horizontal walls, check x-coordinate overlap
        draggedMin = Math.min(draggedWall.start.x, draggedWall.end.x);
        draggedMax = Math.max(draggedWall.start.x, draggedWall.end.x);
        roomWallMin = Math.min(wall.start.x, wall.end.x);
        roomWallMax = Math.max(wall.start.x, wall.end.x);
        
        // Also check if they're on the same y-level (approximately)
        const yDifference = Math.abs(draggedWall.start.y - wall.start.y);
        if (yDifference > 5) return false; // Not the same horizontal line
      } else {
        // For vertical walls, check y-coordinate overlap  
        draggedMin = Math.min(draggedWall.start.y, draggedWall.end.y);
        draggedMax = Math.max(draggedWall.start.y, draggedWall.end.y);
        roomWallMin = Math.min(wall.start.y, wall.end.y);
        roomWallMax = Math.max(wall.start.y, wall.end.y);
        
        // Also check if they're on the same x-level (approximately)
        const xDifference = Math.abs(draggedWall.start.x - wall.start.x);
        if (xDifference > 5) return false; // Not the same vertical line
      }
      
      // Check for overlap: walls overlap if one doesn't end before the other starts
      const hasOverlap = !(draggedMax < roomWallMin || draggedMin > roomWallMax);
      
      if (hasOverlap) {
        console.log('🔍 SHARED WALL CANDIDATE FOUND', {
          draggedWallCoords: `(${draggedWall.start.x.toFixed(1)},${draggedWall.start.y.toFixed(1)})->(${draggedWall.end.x.toFixed(1)},${draggedWall.end.y.toFixed(1)})`,
          roomWallCoords: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
          isHorizontal,
          crossProduct: crossProduct.toFixed(3),
          overlapRange: `${Math.max(draggedMin, roomWallMin).toFixed(1)} to ${Math.min(draggedMax, roomWallMax).toFixed(1)}`
        });
      }
      
      return hasOverlap;
    });
  });
  
  // 🔧 SHARED WALL ALLOWANCE: For shared walls, apply much more relaxed collision detection
  if (isSharedWallResize) {
    console.log('🔧 SHARED WALL DETECTED: Applying relaxed collision rules during detection', {
      totalDistance: totalDistance.toFixed(1),
      allowedMovement: 'significantly_increased'
    });
    
    // For shared walls, allow significant movement - only block if there's a true severe collision
    const relaxedDistance = totalDistance * 0.8; // Allow 80% of intended movement
    return Math.max(minDistance, relaxedDistance);
  }

  // Test at intervals to find collision point
  const steps = Math.max(10, Math.floor(totalDistance / 5));
  
  for (let i = 1; i <= steps; i++) {
    const ratio = i / steps;
    const testPoint = {
      x: originalPoint.x + deltaX * ratio,
      y: originalPoint.y + deltaY * ratio
    };
    
    // Create test wall at this position
    const testWall: Wall = {
      start: (() => {
        switch (resizeDirection) {
          case 'w': // West - moving left edge
          case 'e': // East - moving right edge
            return { x: testPoint.x, y: draggedWall.start.y };
          case 'n': // North - moving top edge
          case 's': // South - moving bottom edge
            return { x: draggedWall.start.x, y: testPoint.y };
          default:
            return testPoint;
        }
      })(),
      end: (() => {
        switch (resizeDirection) {
          case 'w': // West - moving left edge
          case 'e': // East - moving right edge
            return { x: testPoint.x, y: draggedWall.end.y };
          case 'n': // North - moving top edge
          case 's': // South - moving bottom edge
            return { x: draggedWall.end.x, y: testPoint.y };
          default:
            const deltaX = draggedWall.end.x - draggedWall.start.x;
            const deltaY = draggedWall.end.y - draggedWall.start.y;
            return { x: testPoint.x + deltaX, y: testPoint.y + deltaY };
        }
      })(),
      type: draggedWall.type,
      floor: draggedWall.floor
    };
    
    // Check for collisions
    const collision = doesWallIntersectRooms(testWall, otherRooms, resizingRoom, 0);
    if (collision.intersects) {
      // Found collision, use previous safe distance
      maxDistance = totalDistance * Math.max(0, (i - 1) / steps);
      break;
    }
    
    // Also check if test points are inside other rooms
    if (isPointInsideAnyRoom(testWall.start, otherRooms, resizingRoom) ||
        isPointInsideAnyRoom(testWall.end, otherRooms, resizingRoom)) {
      maxDistance = totalDistance * Math.max(0, (i - 1) / steps);
      break;
    }
  }
  
  // Note: Shared wall detection and handling moved to earlier in the function
  
  // 🔧 CONNECTED ROOMS: For rooms that are close but not sharing walls
  const isConnectedResize = maxDistance < minDistance * 3; // Increased threshold
  
  if (isConnectedResize) {
    // For connected rooms, allow reasonable movement with minimal buffer
    const minAllowedMovement = Math.min(30, totalDistance * 0.5); // Increased from 20px and 30%
    const adjustedDistance = Math.max(minAllowedMovement, maxDistance - (minDistance * 0.3)); // Reduced buffer
    
    console.log('🔧 CONNECTED ROOMS: Applying relaxed collision rules', {
      originalMaxDistance: maxDistance.toFixed(1),
      minAllowedMovement: minAllowedMovement.toFixed(1),
      adjustedDistance: adjustedDistance.toFixed(1)
    });
    
    return adjustedDistance;
  } else {
    // Normal collision detection for non-connected rooms
    return Math.max(0, maxDistance - minDistance);
  }
};

/**
 * Main collision detection function for room resizing
 */
export const detectRoomResizeCollision = (
  resizingRoom: Room,
  resizeDirection: string,
  draggedWall: Wall,
  targetPoint: Point,
  allRooms: Room[],
  minDistanceBetweenRooms: number = 0 // Allow rooms to touch each other
): CollisionResult => {
  console.log('🛡️ COLLISION DETECTION: Starting room resize collision check', {
    resizingRoom: resizingRoom.name,
    resizeDirection,
    draggedWallCoords: `(${draggedWall.start.x.toFixed(1)},${draggedWall.start.y.toFixed(1)})->(${draggedWall.end.x.toFixed(1)},${draggedWall.end.y.toFixed(1)})`,
    targetPoint: `(${targetPoint.x.toFixed(1)},${targetPoint.y.toFixed(1)})`,
    resizingRoomWallCount: resizingRoom.walls.length,
    hasComplexShape: resizingRoom.walls.length > 4,
    isSplitWall: !!(draggedWall as any).isSplitOrigin,
    parentWallId: (draggedWall as any).parentWallId
  });
  
  // 🎯 PROFESSIONAL CAD: Skip collision detection for split walls
  // Split walls should be able to move independently like in AutoCAD/Revit
  if ((draggedWall as any).isSplitOrigin && (draggedWall as any).parentWallId) {
    console.log('🎯 SPLIT WALL DETECTED: Allowing independent movement (Professional CAD behavior)', {
      isSplitOrigin: (draggedWall as any).isSplitOrigin,
      parentWallId: (draggedWall as any).parentWallId,
      targetPoint: `(${targetPoint.x.toFixed(1)},${targetPoint.y.toFixed(1)})`
    });
    
    return {
      hasCollision: false,
      constrainedPoint: targetPoint,
      collisionRooms: [],
      maxAllowedResize: Infinity,
      collisionWalls: [],
      isConnected: false,
      connectionLocked: false
    };
  }
  
  // Get other rooms (exclude the resizing room)
  const otherRooms = allRooms.filter(room => room.stableId !== resizingRoom.stableId);
  
  if (otherRooms.length === 0) {
    console.log('✅ COLLISION DETECTION: No other rooms found - resize allowed');
    return {
      hasCollision: false,
      constrainedPoint: targetPoint,
      collisionRooms: [],
      maxAllowedResize: Infinity,
      collisionWalls: [],
      isConnected: false,
      connectionLocked: false
    };
  }
  
  // 🔒 CONNECTION LOCK CHECK: If we have an existing connection, check if it should remain locked
  if (connectionLockState.isLocked && connectionLockState.connectionPoint) {
    const shouldRemainLocked = shouldLockConnection(
      targetPoint,
      connectionLockState.connectionPoint,
      true // Currently locked
    );
    
    if (shouldRemainLocked) {
      console.log('🔒 CONNECTION REMAINS LOCKED', {
        lockPoint: `(${connectionLockState.connectionPoint.x.toFixed(1)},${connectionLockState.connectionPoint.y.toFixed(1)})`,
        targetPoint: `(${targetPoint.x.toFixed(1)},${targetPoint.y.toFixed(1)})`,
        distanceFromLock: Math.hypot(
          targetPoint.x - connectionLockState.connectionPoint.x,
          targetPoint.y - connectionLockState.connectionPoint.y
        ).toFixed(1)
      });
      
      return {
        hasCollision: false,
        constrainedPoint: connectionLockState.connectionPoint,
        collisionRooms: [],
        maxAllowedResize: Infinity,
        collisionWalls: [],
        isConnected: true,
        connectionLocked: true,
        connectionPoint: connectionLockState.connectionPoint
      };
    } else {
      console.log('🔓 CONNECTION LOCK BROKEN - Distance too far');
      connectionLockState.isLocked = false;
      connectionLockState.connectionPoint = null;
    }
  }
  
  // IMMEDIATE SNAP CHECK: Before any complex collision detection, check for simple proximity snapping
  const snapZoneDistance = 25; // Even larger snap zone for immediate connections
  const immediateSnapPoint = findImmediateSnapPoint(targetPoint, otherRooms, resizeDirection, snapZoneDistance);
  
  if (immediateSnapPoint) {
    const snapDistance = Math.hypot(targetPoint.x - immediateSnapPoint.x, targetPoint.y - immediateSnapPoint.y);
    
    // Check if we should establish a connection lock
    const shouldLock = shouldLockConnection(targetPoint, immediateSnapPoint, false);
    
    if (shouldLock) {
      // Establish connection lock
      connectionLockState.isLocked = true;
      connectionLockState.connectionPoint = immediateSnapPoint;
      connectionLockState.lockedRoomId = resizingRoom.stableId || null;
      connectionLockState.lockStartTime = Date.now();
      
      console.log('🔒 CONNECTION LOCK ESTABLISHED', {
        lockPoint: `(${immediateSnapPoint.x.toFixed(1)},${immediateSnapPoint.y.toFixed(1)})`,
        snapDistance: snapDistance.toFixed(1)
      });
    }
    
    console.log('⚡ IMMEDIATE SNAP DETECTED: Snapping to nearby wall point', {
      originalTarget: `(${targetPoint.x.toFixed(1)},${targetPoint.y.toFixed(1)})`,
      snapPoint: `(${immediateSnapPoint.x.toFixed(1)},${immediateSnapPoint.y.toFixed(1)})`,
      snapDistance: snapDistance.toFixed(1),
      connectionLocked: shouldLock
    });
    
    return {
      hasCollision: false,
      constrainedPoint: immediateSnapPoint,
      collisionRooms: [],
      maxAllowedResize: Infinity,
      collisionWalls: [],
      isConnected: true,
      connectionLocked: shouldLock,
      connectionPoint: immediateSnapPoint
    };
  }
  
  // Create the proposed new wall position
  // Enhanced to handle all resize directions properly
  const proposedWall: Wall = {
    start: (() => {
      switch (resizeDirection) {
        case 'w': // West - moving left edge
        case 'e': // East - moving right edge
          return { x: targetPoint.x, y: draggedWall.start.y };
        case 'n': // North - moving top edge
        case 's': // South - moving bottom edge
          return { x: draggedWall.start.x, y: targetPoint.y };
        default:
          // For complex resize directions, use the target point
          return targetPoint;
      }
    })(),
    end: (() => {
      switch (resizeDirection) {
        case 'w': // West - moving left edge
        case 'e': // East - moving right edge
          return { x: targetPoint.x, y: draggedWall.end.y };
        case 'n': // North - moving top edge
        case 's': // South - moving bottom edge
          return { x: draggedWall.end.x, y: targetPoint.y };
        default:
          // For complex resize directions, calculate end point based on original wall direction
          const deltaX = draggedWall.end.x - draggedWall.start.x;
          const deltaY = draggedWall.end.y - draggedWall.start.y;
          return { x: targetPoint.x + deltaX, y: targetPoint.y + deltaY };
      }
    })(),
    type: draggedWall.type,
    floor: draggedWall.floor
  };
  
  console.log('🧪 COLLISION DETECTION: Testing proposed wall position', {
    proposedWallCoords: `(${proposedWall.start.x.toFixed(1)},${proposedWall.start.y.toFixed(1)})->(${proposedWall.end.x.toFixed(1)},${proposedWall.end.y.toFixed(1)})`,
    otherRoomsCount: otherRooms.length
  });
  
  // Check for collisions with snap zone support
  const collision = doesWallIntersectRooms(proposedWall, otherRooms, resizingRoom, snapZoneDistance);
  
  // AGGRESSIVE CONNECTION CHECK: Always try to find connection points when close to other rooms
  const closeToOtherRooms = otherRooms.some(room => 
    room.walls.some(wall => {
      const distStart = Math.min(
        Math.hypot(targetPoint.x - wall.start.x, targetPoint.y - wall.start.y),
        Math.hypot(targetPoint.x - wall.end.x, targetPoint.y - wall.end.y)
      );
      return distStart <= snapZoneDistance;
    })
  );
  
  // Check if we're trying to connect walls (within snap zone but not intersecting OR just close to other rooms)
  if ((collision.isInSnapZone && !collision.intersects) || closeToOtherRooms) {
    console.log('🔗 WALL CONNECTION DETECTED: Allowing connection within snap zone', {
      snapZoneDistance,
      targetPoint: `(${targetPoint.x.toFixed(1)},${targetPoint.y.toFixed(1)})`,
      proposedWallCoords: `(${proposedWall.start.x.toFixed(1)},${proposedWall.start.y.toFixed(1)})->(${proposedWall.end.x.toFixed(1)},${proposedWall.end.y.toFixed(1)})`
    });
    
    // For wall connections, find the closest connection point and snap to it
    const connectionPoint = findBestConnectionPoint(proposedWall, otherRooms, snapZoneDistance, resizeDirection, targetPoint);
    
          if (connectionPoint) {
        const snapDistance = Math.hypot(targetPoint.x - connectionPoint.x, targetPoint.y - connectionPoint.y);
        
        // Check if we should establish a connection lock
        const shouldLock = shouldLockConnection(targetPoint, connectionPoint, false);
        
        if (shouldLock) {
          // Establish connection lock
          connectionLockState.isLocked = true;
          connectionLockState.connectionPoint = connectionPoint;
          connectionLockState.lockedRoomId = resizingRoom.stableId || null;
          connectionLockState.lockStartTime = Date.now();
          
          console.log('🔒 CONNECTION LOCK ESTABLISHED (WALL CONNECTION)', {
            lockPoint: `(${connectionPoint.x.toFixed(1)},${connectionPoint.y.toFixed(1)})`,
            snapDistance: snapDistance.toFixed(1)
          });
        }
        
        console.log('🎯 SNAP CONNECTION APPLIED', {
          originalPoint: `(${targetPoint.x.toFixed(1)},${targetPoint.y.toFixed(1)})`,
          connectionPoint: `(${connectionPoint.x.toFixed(1)},${connectionPoint.y.toFixed(1)})`,
          snapDistance: snapDistance.toFixed(1),
          connectionLocked: shouldLock
        });
        
        return {
          hasCollision: false,
          constrainedPoint: connectionPoint, // Use the connection point instead of original target
          collisionRooms: [],
          maxAllowedResize: Infinity,
          collisionWalls: [],
          isConnected: true,
          connectionLocked: shouldLock,
          connectionPoint: connectionPoint
        };
      } else {
        console.log('🔗 SNAP ZONE DETECTED: Allowing resize but no precise connection found', {
          snapZoneDistance,
          targetPoint: `(${targetPoint.x.toFixed(1)},${targetPoint.y.toFixed(1)})`
        });
        
        // Allow resize but indicate snap zone for potential UI feedback
        return {
          hasCollision: false,
          constrainedPoint: targetPoint,
          collisionRooms: [],
          maxAllowedResize: Infinity,
          collisionWalls: [],
          isConnected: false,
          connectionLocked: false
        };
      }
    }
  
  // Only prevent resize if there's an actual intersection, not just in snap zone
  if (collision.intersects) {
    console.log('⚠️ COLLISION DETECTED: Finding maximum allowed resize distance', {
      intersectingRoomsCount: collision.intersectingRooms.length,
      intersectingRoomNames: collision.intersectingRooms.map(r => r.name)
    });
    
    // Find the maximum allowed resize distance
    const maxDistance = findMaxAllowedResize(
      resizingRoom,
      resizeDirection,
      draggedWall,
      targetPoint,
      otherRooms,
      minDistanceBetweenRooms
    );
    
    // Calculate constrained point
    const originalPoint = (() => {
      switch (resizeDirection) {
        case 'w': // West - moving left edge
        case 'e': // East - moving right edge
          return { x: draggedWall.start.x, y: draggedWall.start.y };
        case 'n': // North - moving top edge
        case 's': // South - moving bottom edge
          return { x: draggedWall.start.x, y: draggedWall.start.y };
        default:
          return { x: draggedWall.start.x, y: draggedWall.start.y };
      }
    })();
      
    const deltaX = targetPoint.x - originalPoint.x;
    const deltaY = targetPoint.y - originalPoint.y;
    const totalDistance = Math.hypot(deltaX, deltaY);
    
    const constrainedRatio = totalDistance > 0 ? maxDistance / totalDistance : 0;
    const constrainedPoint = {
      x: originalPoint.x + deltaX * constrainedRatio,
      y: originalPoint.y + deltaY * constrainedRatio
    };
    
    console.log('🔒 COLLISION CONSTRAINT APPLIED', {
      originalDistance: totalDistance.toFixed(1),
      maxAllowedDistance: maxDistance.toFixed(1),
      constrainedPoint: `(${constrainedPoint.x.toFixed(1)},${constrainedPoint.y.toFixed(1)})`
    });
    
    return {
      hasCollision: true,
      constrainedPoint,
      collisionRooms: collision.intersectingRooms,
      maxAllowedResize: maxDistance,
      collisionWalls: collision.intersectingRooms.flatMap(room => room.walls),
      isConnected: false,
      connectionLocked: false
    };
  }
  
  // 🔒 ROOM INTEGRITY CHECK: Ensure resize won't cause unintended room merging
  // Only apply strict checks for extreme cases to avoid blocking normal resize operations
  const integrityCheck = checkRoomBoundaryIntegrity(resizingRoom, proposedWall, allRooms);
  
  if (!integrityCheck.isValid && integrityCheck.violationType === 'complete_enclosure') {
    // Only block complete room enclosure - allow other cases to proceed with warnings
    console.log('🚫 ROOM INTEGRITY VIOLATION: Resize would cause complete room enclosure - blocking', {
      reason: integrityCheck.reason,
      violationType: integrityCheck.violationType,
      recommendedAction: 'maintain_room_separation'
    });
    
    return {
      hasCollision: true,
      constrainedPoint: draggedWall.start, // Revert to original position
      collisionRooms: integrityCheck.affectedRooms || [],
      maxAllowedResize: 0,
      collisionWalls: [],
      isConnected: false,
      connectionLocked: false
    };
  } else if (!integrityCheck.isValid) {
    // Log warning but allow resize to proceed for less severe issues
    console.log('⚠️ ROOM INTEGRITY WARNING: Potential room boundary issue detected but allowing resize', {
      reason: integrityCheck.reason,
      violationType: integrityCheck.violationType,
      action: 'proceeding_with_caution'
    });
  }
  
  console.log('✅ COLLISION DETECTION: No collision detected and room integrity maintained - resize allowed');
  return {
    hasCollision: false,
    constrainedPoint: targetPoint,
    collisionRooms: [],
    maxAllowedResize: Infinity,
    collisionWalls: [],
    isConnected: false,
    connectionLocked: false
  };
};

/**
 * Check if a room would overlap with other rooms after a resize
 */
export const wouldRoomOverlapAfterResize = (
  resizedRoomWalls: Wall[],
  otherRooms: Room[],
  tolerance: number = 0.1
): { hasOverlap: boolean; overlappingRooms: Room[] } => {
  const overlappingRooms: Room[] = [];
  
  for (const room of otherRooms) {
    // Check if any wall of the resized room intersects with this room
    for (const wall of resizedRoomWalls) {
      const collision = doesWallIntersectRooms(wall, [room], { stableId: 'temp' } as Room, 0);
      if (collision.intersects) {
        overlappingRooms.push(room);
        break;
      }
    }
  }
  
  return {
    hasOverlap: overlappingRooms.length > 0,
    overlappingRooms
  };
};

/**
 * Visual feedback for collision detection
 */
export const drawCollisionFeedback = (
  ctx: CanvasRenderingContext2D,
  collisionResult: CollisionResult,
  scale: number,
  viewOffset: Point
) => {
  if (!collisionResult.hasCollision) return;
  
  ctx.save();
  
  // Draw collision boundary
  ctx.strokeStyle = '#ff4444';
  ctx.lineWidth = 2;
  ctx.setLineDash([5, 5]);
  
  collisionResult.collisionWalls.forEach(wall => {
    const startX = (wall.start.x + viewOffset.x) * scale;
    const startY = (wall.start.y + viewOffset.y) * scale;
    const endX = (wall.end.x + viewOffset.x) * scale;
    const endY = (wall.end.y + viewOffset.y) * scale;
    
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, endY);
    ctx.stroke();
  });
  
  ctx.restore();
};

/**
 * Check if a room resize would maintain proper boundary integrity
 * Prevents unintended room merging during horizontal/vertical resize operations
 * CONSERVATIVE: Only blocks extreme cases to avoid interfering with normal resize
 */
const checkRoomBoundaryIntegrity = (
  resizingRoom: Room,
  proposedWall: Wall,
  allRooms: Room[]
): { 
  isValid: boolean; 
  reason?: string; 
  violationType?: string;
  affectedRooms?: Room[];
} => {
  // Get other rooms (exclude the resizing room)
  const otherRooms = allRooms.filter(room => room.stableId !== resizingRoom.stableId);
  
  if (otherRooms.length === 0) {
    return { isValid: true };
  }
  
  console.log('🔍 ROOM INTEGRITY CHECK: Analyzing resize operation', {
    resizingRoomName: resizingRoom.name,
    resizingRoomCenter: getRoomCenter(resizingRoom.walls),
    proposedWallCoords: `(${proposedWall.start.x.toFixed(1)},${proposedWall.start.y.toFixed(1)})->(${proposedWall.end.x.toFixed(1)},${proposedWall.end.y.toFixed(1)})`,
    otherRoomsCount: otherRooms.length
  });
  
  // For now, be very conservative - only check for extreme wall overlaps
  // Disable the room enclosure check as it's too aggressive for normal resizes
  
  // Check for complete wall overlap that would definitely merge rooms
  for (const otherRoom of otherRooms) {
    for (const otherWall of otherRoom.walls) {
      // Only check for exact wall overlaps that would truly merge boundaries
      if (doWallsCompletelyOverlap(proposedWall, otherWall, 1)) {
        console.log('🚫 ROOM INTEGRITY: Complete wall overlap detected', {
          proposedWall: `(${proposedWall.start.x.toFixed(1)},${proposedWall.start.y.toFixed(1)})->(${proposedWall.end.x.toFixed(1)},${proposedWall.end.y.toFixed(1)})`,
          overlappingWall: `(${otherWall.start.x.toFixed(1)},${otherWall.start.y.toFixed(1)})->(${otherWall.end.x.toFixed(1)},${otherWall.end.y.toFixed(1)})`,
          affectedRoom: otherRoom.name
        });
        
        return {
          isValid: false,
          reason: 'Wall overlap would merge room boundaries',
          violationType: 'boundary_overlap',
          affectedRooms: [otherRoom]
        };
      }
    }
  }
  
  console.log('✅ ROOM INTEGRITY CHECK: No boundary violations detected - resize allowed');
  return { isValid: true };
};

/**
 * Calculate the center point of a room based on its walls
 */
const getRoomCenter = (walls: Wall[]): Point => {
  const points = walls.flatMap(wall => [wall.start, wall.end]);
  const uniquePoints = Array.from(
    new Map(points.map(p => [`${p.x.toFixed(2)},${p.y.toFixed(2)}`, p])).values()
  );
  
  const sumX = uniquePoints.reduce((sum, p) => sum + p.x, 0);
  const sumY = uniquePoints.reduce((sum, p) => sum + p.y, 0);
  
  return {
    x: sumX / uniquePoints.length,
    y: sumY / uniquePoints.length
  };
};

/**
 * Check if two walls completely overlap within tolerance
 */
const doWallsCompletelyOverlap = (wall1: Wall, wall2: Wall, tolerance: number = 1): boolean => {
  // Check if walls are collinear and overlap significantly
  const distance1 = Math.hypot(wall1.start.x - wall2.start.x, wall1.start.y - wall2.start.y);
  const distance2 = Math.hypot(wall1.end.x - wall2.end.x, wall1.end.y - wall2.end.y);
  const distance3 = Math.hypot(wall1.start.x - wall2.end.x, wall1.start.y - wall2.end.y);
  const distance4 = Math.hypot(wall1.end.x - wall2.start.x, wall1.end.y - wall2.start.y);
  
  // Check for exact or nearly exact overlap
  return (distance1 <= tolerance && distance2 <= tolerance) ||
         (distance3 <= tolerance && distance4 <= tolerance);
};